/**
 * Funciones de exportación a Excel para todos los comparativos.
 * Usa la misma librería xlsx que exportSummary.ts.
 * Cada función genera un workbook multi-hoja bien organizado.
 */
import * as XLSX from 'xlsx';
import {
  YearComparisonSummaryDto,
  YearComparisonRowDto,
  MonthComparisonSummaryDto,
  MonthComparisonRowDto,
  AccumulatedComparisonSummaryDto,
  ClientYearComparisonRowDto,
  TopVariationItemDto,
  SummaryYearRowDto,
} from '../../../shared/services/results/types.comparativos';
import { SummaryRow } from '../../../shared/services/results/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function moneyVal(v: number): number {
  return Math.round(v);
}

function pctVal(v: number | null): string {
  if (v === null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function saveWorkbook(wb: XLSX.WorkBook, filename: string) {
  XLSX.writeFile(wb, filename);
}

/** Aplica un ancho mínimo a todas las columnas de una hoja */
function autoWidth(ws: XLSX.WorkSheet, minWidth = 14) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  ws['!cols'] = Array.from({ length: range.e.c + 1 }, () => ({ wch: minWidth }));
}

/** Hoja de resumen KPIs */
function buildKpiSheet(
  periodo1: string,
  periodo2: string,
  ingreso1: number, ingreso2: number,
  gastos1: number,  gastos2: number,
  utilidad1: number, utilidad2: number,
  varIngresoAbs: number, varIngresoPct: number | null,
  varGastosAbs: number,  varGastosPct: number | null,
  varUtilidadAbs: number, varUtilidadPct: number | null,
): XLSX.WorkSheet {
  const rows = [
    ['Indicador', periodo1, periodo2, 'Variación $', 'Variación %'],
    ['Ingreso',   moneyVal(ingreso1),  moneyVal(ingreso2),  moneyVal(varIngresoAbs),  pctVal(varIngresoPct)],
    ['Gastos',    moneyVal(gastos1),   moneyVal(gastos2),   moneyVal(varGastosAbs),   pctVal(varGastosPct)],
    ['Utilidad',  moneyVal(utilidad1), moneyVal(utilidad2), moneyVal(varUtilidadAbs), pctVal(varUtilidadPct)],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  autoWidth(ws, 18);
  return ws;
}

/** Hoja de detalle por cuenta */
function buildDetailSheet(
  rows: Array<{
    tipoCuenta: string;
    cuentaCodigo: string;
    cuentaDescripcion: string;
    valor1: number;
    valor2: number;
    variacionAbsoluta: number;
    variacionPorcentaje: number | null;
  }>,
  label1: string,
  label2: string,
): XLSX.WorkSheet {
  const header = ['Tipo', 'Código', 'Descripción', label1, label2, 'Variación $', 'Variación %'];
  const data = rows.map((r) => [
    r.tipoCuenta,
    r.cuentaCodigo,
    r.cuentaDescripcion,
    moneyVal(r.valor1),
    moneyVal(r.valor2),
    moneyVal(r.variacionAbsoluta),
    pctVal(r.variacionPorcentaje),
  ]);
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  autoWidth(ws, 16);
  return ws;
}

/** Hoja con top N variaciones (favorable / desfavorable) */
function buildTopSheet(
  rows: Array<{
    tipoCuenta: string;
    cuentaCodigo: string;
    cuentaDescripcion: string;
    valor1: number;
    valor2: number;
    variacionAbsoluta: number;
    variacionPorcentaje: number | null;
  }>,
  label1: string,
  label2: string,
  topN: number,
): XLSX.WorkSheet {
  const sorted = [...rows]
    .sort((a, b) => Math.abs(b.variacionAbsoluta) - Math.abs(a.variacionAbsoluta))
    .slice(0, topN);

  const header = ['#', 'Tipo', 'Código', 'Descripción', label1, label2, 'Variación $', 'Variación %'];
  const data = sorted.map((r, i) => [
    i + 1,
    r.tipoCuenta,
    r.cuentaCodigo,
    r.cuentaDescripcion,
    moneyVal(r.valor1),
    moneyVal(r.valor2),
    moneyVal(r.variacionAbsoluta),
    pctVal(r.variacionPorcentaje),
  ]);
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  autoWidth(ws, 16);
  return ws;
}

// ─── Año vs Año ───────────────────────────────────────────────────────────────

export function exportYearVsYearExcel(data: YearComparisonSummaryDto): void {
  const wb = XLSX.utils.book_new();

  // 1. Hoja KPIs
  const kpiWs = buildKpiSheet(
    String(data.anio1), String(data.anio2),
    data.ingresoAnio1, data.ingresoAnio2,
    data.gastosAnio1,  data.gastosAnio2,
    data.utilidadAnio1, data.utilidadAnio2,
    data.varIngresoAbs, data.varIngresoPct,
    data.varGastosAbs,  data.varGastosPct,
    data.varUtilidadAbs, data.varUtilidadPct,
  );
  XLSX.utils.book_append_sheet(wb, kpiWs, 'Resumen KPIs');

  // 2. Hoja detalle todas las cuentas
  const detailRows = data.rows.map((r) => ({
    ...r,
    valor1: r.totalAnio1,
    valor2: r.totalAnio2,
  }));
  const detailWs = buildDetailSheet(detailRows, String(data.anio1), String(data.anio2));
  XLSX.utils.book_append_sheet(wb, detailWs, 'Detalle por Cuenta');

  // 3. Hoja top 10 variaciones
  const topWs = buildTopSheet(detailRows, String(data.anio1), String(data.anio2), 10);
  XLSX.utils.book_append_sheet(wb, topWs, 'Top 10 Variaciones');

  // 4. Hoja ingresos
  const ingRows = detailRows.filter((r) => r.tipoCuenta === 'INGRESO');
  const ingWs = buildDetailSheet(ingRows, String(data.anio1), String(data.anio2));
  XLSX.utils.book_append_sheet(wb, ingWs, 'Ingresos');

  // 5. Hoja gastos
  const gtoRows = detailRows.filter((r) => r.tipoCuenta === 'GASTOS');
  const gtoWs = buildDetailSheet(gtoRows, String(data.anio1), String(data.anio2));
  XLSX.utils.book_append_sheet(wb, gtoWs, 'Gastos');

  saveWorkbook(wb, `Comparativo_${data.anio1}_vs_${data.anio2}.xlsx`);
}

// ─── Mes vs Mes ───────────────────────────────────────────────────────────────

export function exportMonthVsMonthExcel(data: MonthComparisonSummaryDto): void {
  const wb = XLSX.utils.book_new();

  const p1 = `${data.mes1} ${data.anio1}`;
  const p2 = `${data.mes2} ${data.anio2}`;

  // 1. KPIs
  const kpiWs = buildKpiSheet(
    p1, p2,
    data.ingresoMes1, data.ingresoMes2,
    data.gastosMes1,  data.gastosMes2,
    data.utilidadMes1, data.utilidadMes2,
    data.varIngresoAbs, data.varIngresoPct,
    data.varGastosAbs,  data.varGastosPct,
    data.varUtilidadAbs, data.varUtilidadPct,
  );
  XLSX.utils.book_append_sheet(wb, kpiWs, 'Resumen KPIs');

  // 2. Detalle
  const detailRows = data.rows.map((r) => ({
    ...r,
    valor1: r.valorMes1,
    valor2: r.valorMes2,
  }));
  const detailWs = buildDetailSheet(detailRows, p1, p2);
  XLSX.utils.book_append_sheet(wb, detailWs, 'Detalle por Cuenta');

  // 3. Top 10
  const topWs = buildTopSheet(detailRows, p1, p2, 10);
  XLSX.utils.book_append_sheet(wb, topWs, 'Top 10 Variaciones');

  // 4. Ingresos
  const ingWs = buildDetailSheet(detailRows.filter((r) => r.tipoCuenta === 'INGRESO'), p1, p2);
  XLSX.utils.book_append_sheet(wb, ingWs, 'Ingresos');

  // 5. Gastos
  const gtoWs = buildDetailSheet(detailRows.filter((r) => r.tipoCuenta === 'GASTOS'), p1, p2);
  XLSX.utils.book_append_sheet(wb, gtoWs, 'Gastos');

  const safe = (s: string) => s.replace(/\s+/g, '_');
  saveWorkbook(wb, `Comparativo_${safe(p1)}_vs_${safe(p2)}.xlsx`);
}

// ─── Acumulado ────────────────────────────────────────────────────────────────

export function exportAccumulatedExcel(
  data: AccumulatedComparisonSummaryDto,
  periodo1Label: string,
  periodo2Label: string,
): void {
  const wb = XLSX.utils.book_new();

  // 1. KPIs
  const kpiWs = buildKpiSheet(
    periodo1Label, periodo2Label,
    data.ingresoAnio1, data.ingresoAnio2,
    data.gastosAnio1,  data.gastosAnio2,
    data.utilidadAnio1, data.utilidadAnio2,
    data.varIngresoAbs, data.varIngresoPct,
    data.varGastosAbs,  data.varGastosPct,
    data.varUtilidadAbs, data.varUtilidadPct,
  );
  XLSX.utils.book_append_sheet(wb, kpiWs, 'Resumen KPIs');

  // 2. Detalle
  const detailRows = data.rows.map((r) => ({
    ...r,
    valor1: r.totalAnio1,
    valor2: r.totalAnio2,
  }));
  const detailWs = buildDetailSheet(detailRows, periodo1Label, periodo2Label);
  XLSX.utils.book_append_sheet(wb, detailWs, 'Detalle por Cuenta');

  // 3. Top 10
  const topWs = buildTopSheet(detailRows, periodo1Label, periodo2Label, 10);
  XLSX.utils.book_append_sheet(wb, topWs, 'Top 10 Variaciones');

  const safe = (s: string) => s.replace(/[\s–]+/g, '_');
  saveWorkbook(wb, `Acumulado_${safe(periodo1Label)}_vs_${safe(periodo2Label)}.xlsx`);
}

// ─── Clientes por Año ─────────────────────────────────────────────────────────

export function exportClientsYearExcel(
  rows: ClientYearComparisonRowDto[],
  anio1: number,
  anio2: number,
): void {
  const wb = XLSX.utils.book_new();

  // 1. Hoja completa con todos los clientes
  const header = [
    'Cliente',
    `Ingreso ${anio1}`, `Ingreso ${anio2}`, 'Var Ingreso $', 'Var Ingreso %',
    `Gastos ${anio1}`,  `Gastos ${anio2}`,  'Var Gastos $',  'Var Gastos %',
    `Utilidad ${anio1}`, `Utilidad ${anio2}`, 'Var Utilidad $', 'Var Utilidad %',
  ];
  const data = rows
    .sort((a, b) => b.utilidadAnio2 - a.utilidadAnio2)
    .map((r) => [
      r.cliente,
      moneyVal(r.ingresoAnio1), moneyVal(r.ingresoAnio2), moneyVal(r.varIngresoAbs),  pctVal(r.varIngresoPct),
      moneyVal(r.gastosAnio1),  moneyVal(r.gastosAnio2),  moneyVal(r.varGastosAbs),   pctVal(r.varGastosPct),
      moneyVal(r.utilidadAnio1), moneyVal(r.utilidadAnio2), moneyVal(r.varUtilidadAbs), pctVal(r.varUtilidadPct),
    ]);
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  autoWidth(ws, 16);
  XLSX.utils.book_append_sheet(wb, ws, 'Clientes');

  // 2. Hoja top 10 por utilidad
  const top10Header = ['#', 'Cliente', `Utilidad ${anio1}`, `Utilidad ${anio2}`, 'Var Utilidad $', 'Var Utilidad %'];
  const top10Data = rows
    .sort((a, b) => b.utilidadAnio2 - a.utilidadAnio2)
    .slice(0, 10)
    .map((r, i) => [
      i + 1,
      r.cliente,
      moneyVal(r.utilidadAnio1),
      moneyVal(r.utilidadAnio2),
      moneyVal(r.varUtilidadAbs),
      pctVal(r.varUtilidadPct),
    ]);
  const top10Ws = XLSX.utils.aoa_to_sheet([top10Header, ...top10Data]);
  autoWidth(top10Ws, 18);
  XLSX.utils.book_append_sheet(wb, top10Ws, 'Top 10 Utilidad');

  // 3. Hoja mayores variaciones
  const varHeader = ['#', 'Cliente', `Utilidad ${anio1}`, `Utilidad ${anio2}`, 'Var Utilidad $', 'Var Utilidad %'];
  const varData = [...rows]
    .sort((a, b) => Math.abs(b.varUtilidadAbs) - Math.abs(a.varUtilidadAbs))
    .slice(0, 10)
    .map((r, i) => [
      i + 1,
      r.cliente,
      moneyVal(r.utilidadAnio1),
      moneyVal(r.utilidadAnio2),
      moneyVal(r.varUtilidadAbs),
      pctVal(r.varUtilidadPct),
    ]);
  const varWs = XLSX.utils.aoa_to_sheet([varHeader, ...varData]);
  autoWidth(varWs, 18);
  XLSX.utils.book_append_sheet(wb, varWs, 'Mayores Variaciones');

  saveWorkbook(wb, `Clientes_${anio1}_vs_${anio2}.xlsx`);
}

// ─── Top Variaciones ──────────────────────────────────────────────────────────

// ─── Tabla Resumen con Comparativo ───────────────────────────────────────────

export function exportResultsSummaryExcel(params: {
  anio: number;
  ingreso: SummaryRow | undefined;
  gastos: SummaryRow | undefined;
  visibleMonthKeys: string[];
  visibleMonthLabels: string[];
  totalByMonth: number[];
  grandTotal: number;
  showComparison: boolean;
  anioBase: number;
  compMap: Record<string, SummaryYearRowDto>;
}): void {
  const { anio, ingreso, gastos, visibleMonthKeys, visibleMonthLabels, totalByMonth, grandTotal, showComparison, anioBase, compMap } = params;
  const wb = XLSX.utils.book_new();

  // 1. Hoja resumen por mes
  const summaryHeader = showComparison
    ? ['Tipo', ...visibleMonthLabels.flatMap((m) => [`${m} ${anioBase}`, `${m} ${anio}`, `${m} Var $`, `${m} Var %`]), 'Total']
    : ['Tipo', ...visibleMonthLabels, 'Total'];

  const ingresoRowData = showComparison
    ? visibleMonthKeys.flatMap((m) => {
        const v2 = ingreso?.valuesByMonth[m] ?? 0;
        const v1 = compMap['INGRESO']?.valuesByMonthAnio1[m] ?? 0;
        const varAbs = v2 - v1;
        const varPct = Math.abs(v1) > 0.01 ? pctVal(((v2 - v1) / Math.abs(v1)) * 100) : '—';
        return [moneyVal(v1), moneyVal(v2), moneyVal(varAbs), varPct];
      })
    : visibleMonthKeys.map((m) => moneyVal(ingreso?.valuesByMonth[m] ?? 0));

  const gastosRowData = showComparison
    ? visibleMonthKeys.flatMap((m) => {
        const v2 = -Math.abs(gastos?.valuesByMonth[m] ?? 0);
        const v1 = -Math.abs(compMap['GASTOS']?.valuesByMonthAnio1[m] ?? 0);
        const varAbs = v2 - v1;
        const varPct = Math.abs(v1) > 0.01 ? pctVal(((v2 - v1) / Math.abs(v1)) * 100) : '—';
        return [moneyVal(v1), moneyVal(v2), moneyVal(varAbs), varPct];
      })
    : visibleMonthKeys.map((m) => moneyVal(-Math.abs(gastos?.valuesByMonth[m] ?? 0)));

  const aoa = [
    summaryHeader,
    ['INGRESO', ...ingresoRowData, moneyVal(ingreso?.total ?? 0)],
    ['GASTOS', ...gastosRowData, moneyVal(-Math.abs(gastos?.total ?? 0))],
    ['Utilidad', ...totalByMonth.map((v) => moneyVal(v)), moneyVal(grandTotal)],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  autoWidth(ws, 14);
  XLSX.utils.book_append_sheet(wb, ws, `Resumen ${anio}`);

  saveWorkbook(wb, `Tabla_Resumen_${anio}${showComparison ? `_vs_${anioBase}` : ''}.xlsx`);
}

export function exportTopVariationsExcel(
  items: TopVariationItemDto[],
  label1: string,
  label2: string,
): void {
  const wb = XLSX.utils.book_new();

  const header = ['#', 'Tipo', 'Código', 'Descripción', label1, label2, 'Variación $', 'Variación %', 'Favorable?'];
  const data = items.map((it, i) => {
    const favorable =
      (it.tipoCuenta === 'INGRESO' && it.variacionAbsoluta > 0) ||
      (it.tipoCuenta === 'GASTOS'  && it.variacionAbsoluta < 0);
    return [
      i + 1,
      it.tipoCuenta,
      it.cuentaCodigo,
      it.cuentaDescripcion,
      moneyVal(it.valor1),
      moneyVal(it.valor2),
      moneyVal(it.variacionAbsoluta),
      pctVal(it.variacionPorcentaje),
      favorable ? 'Sí' : 'No',
    ];
  });

  // 1. Hoja todas las variaciones
  const allWs = XLSX.utils.aoa_to_sheet([header, ...data]);
  autoWidth(allWs, 16);
  XLSX.utils.book_append_sheet(wb, allWs, 'Top Variaciones');

  // 2. Hoja solo favorables
  const favData = data.filter((r) => r[8] === 'Sí');
  if (favData.length > 0) {
    const favWs = XLSX.utils.aoa_to_sheet([header, ...favData]);
    autoWidth(favWs, 16);
    XLSX.utils.book_append_sheet(wb, favWs, 'Favorables');
  }

  // 3. Hoja solo desfavorables
  const desfData = data.filter((r) => r[8] === 'No');
  if (desfData.length > 0) {
    const desfWs = XLSX.utils.aoa_to_sheet([header, ...desfData]);
    autoWidth(desfWs, 16);
    XLSX.utils.book_append_sheet(wb, desfWs, 'Desfavorables');
  }

  const safe = (s: string) => s.replace(/[\s–]+/g, '_');
  saveWorkbook(wb, `Top_Variaciones_${safe(label1)}_vs_${safe(label2)}.xlsx`);
}
